import React, { useMemo, useRef } from 'react';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import classNames from 'classnames';

import { ButtonSplitGroup } from '@components/Buttons';
import { DefaultSpinnerCenter } from '@components/Loader/Loader';
import { renderHeaderWithHover } from '@components/Table/Columns';
import { BootstrapTable } from '@components/Table/Table';
import { AutoScrollToQuery } from '@features/AutoScrollTo';
import {
  hidePlanDetails,
  hidePlanPrefixes,
  planDetailsApi,
  showPlanDetails,
  showPlanPrefixes,
} from '@reducers/plan/plan';

const renderActionCell = ({ row }) => {
  const dispatch = useDispatch();

  const { planInfo } = useSelector((state) => state.plan);
  const { currentPlan, details, prefixes } = planInfo.showState;

  const plan = {
    id: row.values.id,
    type: row.original.plan_type,
    name: row.values.name,
    outboundRates: row.original.lt_available,
  };
  const isCurrent =
    currentPlan.id === plan.id && currentPlan.type === plan.type;
  const isDetailsActive = isCurrent && details;
  const isPrefixesActive = isCurrent && prefixes;
  return (
    <ButtonSplitGroup>
      <Button
        variant="didxl"
        onClick={() => {
          if (!isCurrent) {
            dispatch(hidePlanPrefixes());
            dispatch(hidePlanDetails());
          }
          if (isDetailsActive) {
            dispatch(hidePlanDetails());
            return;
          }
          dispatch(hidePlanPrefixes());
          dispatch(showPlanDetails(plan));
        }}
        className={classNames({ active: isDetailsActive })}
        size="sm"
      >
        Info
      </Button>
      <Button
        variant="didxl"
        onClick={() => {
          if (!isCurrent) {
            dispatch(hidePlanPrefixes());
            dispatch(hidePlanDetails());
          }
          if (isPrefixesActive) {
            dispatch(hidePlanPrefixes());
            return;
          }
          dispatch(hidePlanDetails());
          dispatch(showPlanPrefixes(plan));
        }}
        size="sm"
        className={classNames({ active: isPrefixesActive })}
      >
        Select
      </Button>
    </ButtonSplitGroup>
  );
};

const emptyMessageContent = () => (
  <span className="textRed fw-bold normalFontSize">
    Your filters did not result in a matching plan, you can update the filters
    to show available Plans with other criteria.
  </span>
);

export const PlanTable = ({ data, isLoading, countryName }) => {
  const contentElem = useRef(null);
  const [searchParams, _] = useSearchParams();
  const country = searchParams.get('country');
  const query = planDetailsApi.useGetFieldDescriptionQuery();
  const fieldDescriptions = query.data || {};
  const columns = useMemo(
    () => [
      {
        id: 'id',
        Header: '',
        accessor: 'id',
        className: 'hidden',
      },
      {
        Header: () =>
          renderHeaderWithHover('Plan', 'plan', fieldDescriptions, true),
        accessor: 'name',
        className: 'col-md-1',
      },
      {
        id: 'didType',
        Header: () =>
          renderHeaderWithHover('Type', 'type', fieldDescriptions, true),
        accessor: 'type.name',
        className: 'col-md-1',
      },
      {
        id: 'typeOfService',
        Header: '',
      },
      {
        id: 'lt',
        Header: () =>
          renderHeaderWithHover(
            'LT',
            'local_termination',
            fieldDescriptions,
            true
          ),
        accessor: 'lt_available',
        className: 'col-md-1 text-center',
      },
      {
        id: 'pstn',
        Header: () =>
          renderHeaderWithHover('PSTN', 'pstn', fieldDescriptions, true),
        accessor: 'pstn_available',
        className: 'col-md-1 text-center',
      },
      {
        id: 'sms',
        Header: () =>
          renderHeaderWithHover('SMS', 'sms', fieldDescriptions, true),
        accessor: 'sms_available',
        className: 'col-md-1 text-center',
      },
      {
        Header: () =>
          renderHeaderWithHover('NRC', 'nrc', fieldDescriptions, true),
        accessor: 'nrc',
        className: 'col-md-1',
      },
      {
        Header: () =>
          renderHeaderWithHover('MRC', 'mrc', fieldDescriptions, true),
        accessor: 'mrc',
        className: 'col-md-1',
      },
      {
        Header: () =>
          renderHeaderWithHover('Porting', 'porting', fieldDescriptions, true),
        accessor: 'porting',
        className: 'col-md-1 text-center',
      },
      {
        Header: () =>
          renderHeaderWithHover(
            'Leadtime',
            'leadtime',
            fieldDescriptions,
            true
          ),
        accessor: 'leadtime',
        className: 'col-md-1 text-center',
      },
      {
        Header: renderHeaderWithHover(
          'Leadtime if no stock',
          'leadtime_no_stock',
          fieldDescriptions
        ),
        accessor: 'leadtime_no_stock',
        className: 'col-md-1 text-center',
      },
      {
        Header: () =>
          renderHeaderWithHover('Stock', 'stock', fieldDescriptions, true),
        accessor: 'stock',
        className: 'col-md-1 text-center',
      },
      {
        Header: () =>
          renderHeaderWithHover('Action', 'action', fieldDescriptions, true),
        Cell: renderActionCell,
        accessor: 'action',
        className: 'col-md-2',
      },
    ],
    []
  );
  if (!country) {
    return null;
  }
  return (
    <div className="mt-2">
      {isLoading ? (
        <Row className="text-center">
          <Col ref={contentElem}>
            <DefaultSpinnerCenter />
          </Col>
        </Row>
      ) : null}
      {data && !isLoading && data.length >= 0 ? (
        <Card>
          <Card.Header className="bg-didxl text-white">
            {`Available Plans for ${countryName}`}
          </Card.Header>
          <Card.Body ref={contentElem}>
            <BootstrapTable
              columns={columns}
              data={data}
              tableProps={() => ({
                responsive: true,
                bordered: true,
                className: 'normalFontSize',
              })}
              hiddenColumns="id,typeOfService"
              emptyMessageContent={emptyMessageContent}
            />
          </Card.Body>
        </Card>
      ) : null}
      <AutoScrollToQuery
        query={{ data, isLoading, isFetching: isLoading }}
        ref={contentElem}
      />
    </div>
  );
};
